/**
 * chatRunStore.js
 *
 * A module-level (outside-React) store that owns the lifecycle of an in-flight
 * AI chat run. The agent loop lives here, NOT inside <ChatInterface>, so a run
 * keeps streaming and the result is never lost when the user navigates away from
 * the "Personal Analyst" tab.
 *
 * Persistence is now SERVER-SIDE (the backend saves each completed turn to the
 * conversation on the final SSE event). This store therefore holds run state
 * purely IN MEMORY per conversation id and no longer writes sessionStorage:
 *   - it streams to completion regardless of what is mounted,
 *   - it keeps the messages in memory and notifies whichever <ChatInterface> is
 *     subscribed, so a tab you return to mid-stream resumes live, and
 *   - on the final event it stamps the server message ids onto the turn so the
 *     UI can immediately wire feedback.
 *
 * A component seeds the store from the server (seedMessages) when it opens a
 * conversation; an interrupted stream only loses the in-flight turn (the server
 * never persisted it), consistent with the "history survives refresh" contract.
 *
 * One run per conversation id.
 */

/** conversationId -> { messages, running, streamingId, subscribers:Set, startedAt, elapsed } */
const runs = new Map();

const ensureRun = (chatId) => {
  let run = runs.get(chatId);
  if (!run) {
    run = {
      messages: [],
      running: false,
      streamingId: null,
      subscribers: new Set(),
      startedAt: null,
      elapsed: null,
    };
    runs.set(chatId, run);
  }
  return run;
};

/** Notify any mounted subscriber (in-memory only; the server owns persistence). */
const commit = (chatId) => {
  const run = runs.get(chatId);
  if (!run) return;
  const snapshot = {
    messages: run.messages,
    running: run.running,
    elapsed: run.elapsed,
  };
  run.subscribers.forEach((cb) => {
    try {
      cb(snapshot);
    } catch (_) {
      // a dead subscriber must never break the run
    }
  });
};

// ── message mutations (mirror the semantics the chat UI used to own) ─────────

const upsertStatus = (run, payload) => {
  if (!payload?.message) return;
  const last = run.messages[run.messages.length - 1];
  const entry = {
    role: "status",
    content: payload.message,
    stage: payload.stage || (last?.role === "status" ? last.stage : ""),
    timestamp: new Date(),
  };
  if (last && last.role === "status") {
    entry.id = last.id;
    run.messages = [...run.messages.slice(0, -1), entry];
  } else {
    entry.id = `status-${Date.now()}`;
    run.messages = [...run.messages, entry];
  }
};

const replaceStatusWithAssistant = (run, assistantMessage) => {
  const last = run.messages[run.messages.length - 1];
  if (last && last.role === "status") {
    run.messages = [...run.messages.slice(0, -1), assistantMessage];
  } else {
    run.messages = [...run.messages, assistantMessage];
  }
};

const startStreamingMessage = (run) => {
  const sid = Date.now() + 1;
  run.streamingId = sid;
  const msg = {
    id: sid,
    role: "assistant",
    content: "",
    responseMeta: {},
    structuredTable: {},
    assets: {},
    plots: [],
    isStreaming: true,
    timestamp: new Date(),
  };
  const last = run.messages[run.messages.length - 1];
  if (last && last.role === "status") {
    run.messages = [...run.messages.slice(0, -1), msg];
  } else {
    run.messages = [...run.messages, msg];
  }
  return sid;
};

const appendToken = (run, text) => {
  const sid = run.streamingId;
  if (!sid) return;
  const idx = run.messages.findIndex((m) => m.id === sid);
  if (idx === -1) return;
  const updated = [...run.messages];
  updated[idx] = { ...updated[idx], content: (updated[idx].content || "") + text };
  run.messages = updated;
};

/** Stamp the server id from the final payload onto the just-completed user turn. */
const stampUserServerId = (run, userMessageId) => {
  if (!userMessageId) return;
  for (let i = run.messages.length - 1; i >= 0; i--) {
    if (run.messages[i].role === "user" && !run.messages[i].serverId) {
      const updated = [...run.messages];
      updated[i] = { ...updated[i], serverId: userMessageId };
      run.messages = updated;
      return;
    }
  }
};

const finalizeStreamingMessage = (run, data) => {
  const sid = run.streamingId;
  if (!sid) return;
  const idx = run.messages.findIndex((m) => m.id === sid);
  if (idx !== -1) {
    const updated = [...run.messages];
    const existing = updated[idx];
    updated[idx] = {
      ...existing,
      content: data.markdown || data.response || existing.content,
      responseMeta: data.response_meta || existing.responseMeta || {},
      structuredTable:
        data.structured_table ||
        data.response_meta?.structured_table ||
        data.workflow?.structured_table ||
        existing.structuredTable ||
        {},
      assets: data.assets || existing.assets || {},
      plots: data.plots || existing.plots || [],
      serverId: data.assistant_message_id || existing.serverId || null,
      feedback: 0,
      isStreaming: false,
    };
    run.messages = updated;
  }
  run.streamingId = null;
  stampUserServerId(run, data.user_message_id);
};

const buildAssistantMessage = (data) => ({
  id: Date.now() + 1,
  role: "assistant",
  content: data.markdown || data.response,
  responseMeta: data.response_meta || {},
  structuredTable:
    data.structured_table ||
    data.response_meta?.structured_table ||
    data.workflow?.structured_table ||
    {},
  assets: data.assets || {},
  plots: data.plots || [],
  serverId: data.assistant_message_id || null,
  feedback: 0,
  timestamp: new Date(),
});

/**
 * Forward the finished turn to the live onAssistant callback so a mounted
 * sidebar can refresh (title / snippet / order come from the server). Carries
 * the server conversation id from the final payload.
 */
const afterAssistant = (chatId, assistantMessage, data, onAssistant) => {
  if (typeof onAssistant === "function") {
    try {
      onAssistant({ chatId, assistantMessage, conversationId: data.conversation_id });
    } catch (_) {
      // best-effort live update
    }
  }
};

const logSseEvent = (payload) => {
  if (payload.type === "status") {
    console.log(`%c[AI Status] %c${payload.stage}%c: ${payload.message}`, "color: #888", "color: #4CAF50; font-weight: bold", "color: inherit");
  } else if (payload.type === "final") {
    console.log("%c[AI Complete] ✅ Response received", "color: #4CAF50; font-weight: bold");
  }
};

// ── public API ───────────────────────────────────────────────────────────────

/** Is a run currently in flight for this conversation? */
export const isRunning = (chatId) => !!runs.get(chatId)?.running;

/**
 * Seed the in-memory messages for a conversation from the server (on open).
 * A no-op while a run is in flight so a live stream is never clobbered.
 */
export const seedMessages = (chatId, messages) => {
  const run = ensureRun(chatId);
  if (run.running) return;
  run.messages = Array.isArray(messages) ? messages : [];
  commit(chatId);
};

/**
 * Subscribe a mounted <ChatInterface> to a conversation. The callback fires
 * immediately with the current snapshot ({ messages, running, elapsed }) and on
 * every subsequent update. Returns an unsubscribe fn; unsubscribing does NOT
 * stop an in-flight run.
 */
export const subscribe = (chatId, cb) => {
  const run = ensureRun(chatId);
  run.subscribers.add(cb);
  cb({ messages: run.messages, running: run.running, elapsed: run.elapsed });
  return () => {
    run.subscribers.delete(cb);
  };
};

/**
 * Kick off an agent run for `chatId` (a server conversation id). Fire-and-forget:
 * it streams to completion regardless of whether a component stays mounted.
 *
 * @param {number} chatId  server conversation id
 * @param {object} opts
 * @param {(url:string, init:object)=>Promise<Response>} opts.fetchWithAuth
 * @param {string} opts.endpoint        SSE streaming endpoint
 * @param {object} opts.body            request body (message, conversation_id, ...)
 * @param {Array}  opts.baseMessages    messages including the just-sent user turn
 * @param {Function} [opts.recordActivity]
 * @param {Function} [opts.onAssistant] live callback for sidebar refresh
 */
export const startRun = async (chatId, opts) => {
  const { fetchWithAuth, endpoint, body, baseMessages, recordActivity, onAssistant } = opts;
  const run = ensureRun(chatId);
  run.messages = baseMessages.slice();
  run.running = true;
  run.streamingId = null;
  run.startedAt = Date.now();
  run.elapsed = null;
  commit(chatId);

  try {
    console.log("%c[AI Request] 🚀 Streaming message to AI agent...", "color: #2196F3; font-weight: bold");
    const response = await fetchWithAuth(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok || !response.body) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("text/event-stream")) {
      const data = await response.json();
      const assistantMessage = buildAssistantMessage(data);
      replaceStatusWithAssistant(run, assistantMessage);
      stampUserServerId(run, data.user_message_id);
      commit(chatId);
      afterAssistant(chatId, assistantMessage, data, onAssistant);
      recordActivity?.();
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const part of parts) {
        const lines = part.split("\n").filter(Boolean);
        let eventType = "message";
        const dataLines = [];
        for (const line of lines) {
          if (line.startsWith("event:")) {
            eventType = line.replace("event:", "").trim();
          } else if (line.startsWith("data:")) {
            dataLines.push(line.replace("data:", "").trim());
          }
        }
        if (!dataLines.length) continue;
        // A truncated or malformed frame must not abort the whole run: skip it
        // and keep reading the stream (same guard as fieldInsightStore).
        let payload;
        try {
          payload = JSON.parse(dataLines.join("\n"));
        } catch (_) {
          continue;
        }
        logSseEvent(payload);

        if (eventType === "token" && payload.type === "token") {
          if (!run.streamingId) startStreamingMessage(run);
          appendToken(run, payload.text || "");
          commit(chatId);
        } else if (eventType === "stream_end" && payload.type === "stream_end") {
          // metadata arrives with the final event
        } else if (eventType === "status" && payload.type === "status") {
          upsertStatus(run, payload);
          commit(chatId);
        } else if (eventType === "final" && payload.type === "final") {
          const data = payload.payload || {};
          let assistantMessage;
          if (run.streamingId) {
            finalizeStreamingMessage(run, data);
            assistantMessage =
              run.messages.find((m) => m.role === "assistant" && !m.isStreaming) ||
              buildAssistantMessage(data);
          } else {
            assistantMessage = buildAssistantMessage(data);
            replaceStatusWithAssistant(run, assistantMessage);
            stampUserServerId(run, data.user_message_id);
          }
          commit(chatId);
          afterAssistant(chatId, assistantMessage, data, onAssistant);
          recordActivity?.();
        } else if (eventType === "error") {
          console.error("%c[AI Error]", "color: #f44336; font-weight: bold", payload);
          replaceStatusWithAssistant(run, {
            id: Date.now() + 1,
            role: "assistant",
            content: "Sorry, I encountered an error. Please try again.",
            timestamp: new Date(),
          });
          commit(chatId);
        }
      }
    }
  } catch (error) {
    console.error("Error sending message:", error);
    run.messages = [
      ...run.messages,
      {
        id: Date.now() + 1,
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
      },
    ];
    commit(chatId);
  } finally {
    run.running = false;
    run.streamingId = null;
    run.elapsed = Math.max(0, Math.floor((Date.now() - (run.startedAt || Date.now())) / 1000));
    commit(chatId);
  }
};
